import React from "react";

import {
    CheckCircleIcon,
    MoonIcon,
    SunIcon,
    XCircleIcon,
} from "@heroicons/react/24/outline";
import {XMarkIcon} from "@heroicons/react/24/solid";
import {
    AnimatePresence,
    MotionValue,
    animate,
    motion,
    useMotionValue,
    useScroll,
    useTransform,
} from "framer-motion";

import {
    Notification,
    dismissNotification,
    getNotifications,
} from "@client/features/notifications";

import {ActionBar} from "@client/components/ActionBar";
import type {IconType} from "@client/components/Icon";
import {SimpleIcon} from "@client/components/Icon";
import {MenuIcon} from "@client/components/Navigation";
import {useActions} from "@client/components/Scene";
import {TouchHandler} from "@client/components/TouchHandler";
import type {RouterRenderPropsArgs, TransitionFn} from "@client/gotterdammerung";
import * as hardware from "@client/hardware";
import {ScrollContainerContext} from "@client/journeys/contexts";
import {useAppDispatch, useAppSelector} from "@client/store";
import {classNames} from "@reactivated";

function clamp(value: number, min: number, max: number) {
    return Math.min(Math.max(value, min), max);
}

function useBoundedScroll(
    threshold: number,
    container: React.RefObject<HTMLElement | null>,
) {
    const {scrollY} = useScroll({container});
    const scrollYBounded = useMotionValue(0);
    const scrollYBoundedProgress = useTransform(scrollYBounded, [0, threshold], [0, 1]);

    React.useEffect(() => {
        const unsubscribe = scrollY.on("change", (current) => {
            const previous = scrollY.getPrevious() ?? 0;
            const diff = current - previous;
            const newScrollYBounded = scrollYBounded.get() + diff;

            scrollYBounded.set(clamp(newScrollYBounded, 0, threshold));
        });
        return () => unsubscribe();
    }, [threshold, scrollY, scrollYBounded]);

    return {scrollY, scrollYBoundedProgress};
}

interface ScrollContainerRenderProps {
    scrollY: MotionValue<number>;
    scrollYBoundedProgress: MotionValue<number>;
    hasOverflow: boolean;
}

export function ScrollContainer(props: {
    className?: string;
    children: (renderProps: ScrollContainerRenderProps) => React.ReactNode;
}) {
    const scrollRef = React.useRef<HTMLDivElement>(null);
    const [hasOverflow, setHasOverflow] = React.useState(false);
    const {scrollY, scrollYBoundedProgress} = useBoundedScroll(200, scrollRef);
    const scrollContext = React.useMemo(
        () => ({
            scrollY,
            scrollYBoundedProgress,
            scrollTo: (top: number, duration = 0.15) => {
                if (scrollRef.current == null) return;
                const from = scrollRef.current.scrollTop;
                animate(from, top, {
                    duration,
                    ease: "easeOut",
                    onUpdate: (value) => scrollRef.current?.scrollTo(0, value),
                });
            },
        }),
        [scrollY, scrollYBoundedProgress],
    );

    React.useEffect(() => {
        const element = scrollRef.current;
        if (element == null) return;

        const checkOverflow = () => {
            // The always-scrollable pseudo element adds exactly 1px of
            // overflow; only report overflow beyond it.
            setHasOverflow(element.scrollHeight - element.clientHeight > 1);
        };

        checkOverflow();
        const observer = new ResizeObserver(checkOverflow);
        observer.observe(element);

        return () => observer.disconnect();
    }, []);

    return (
        <ScrollContainerContext.Provider value={scrollContext}>
            <div
                ref={scrollRef}
                className={classNames(
                    // Vertical-only: overflow-auto on both axes lets iOS grab
                    // the x-axis on diagonal pans even without real overflow.
                    "scrollbar-hidden flex min-h-0 flex-1 flex-col overflow-y-auto overflow-x-hidden",
                    // The absolutely positioned 1px past the bottom edge keeps
                    // the container scrollable on iOS even when content fits
                    // (an in-flow spacer would be absorbed by the flex-1
                    // view), so the rubber band stays local instead of
                    // chaining to the webview body.
                    "relative overscroll-y-contain after:absolute after:top-full after:h-px after:w-px",
                    props.className,
                )}
            >
                {props.children({scrollY, scrollYBoundedProgress, hasOverflow})}
            </div>
        </ScrollContainerContext.Provider>
    );
}

function SimpleNotification({
    notification,
    onDismiss,
}: {
    notification: Notification;
    onDismiss?: () => void;
}) {
    const Icon = notification.level_tag === "success" ? CheckCircleIcon : XCircleIcon;

    const iconColor =
        notification.level_tag === "success" ? "text-green-500" : "text-red-500";

    return (
        <div
            className="
                inline-flex items-center gap-2 rounded-xl border border-gray-200/90
                bg-white/95 px-4 py-2 backdrop-blur-md
            "
        >
            <Icon className={classNames("size-5", iconColor)} />
            <span className="text-sm font-medium whitespace-pre-line text-gray-900">
                {notification.message}
            </span>
            {onDismiss != null && (
                <button
                    type="button"
                    onClick={onDismiss}
                    aria-label="Dismiss notification"
                    className="
                        ml-1 text-gray-400
                        hover:text-gray-600
                    "
                >
                    <XMarkIcon className="size-4" />
                </button>
            )}
        </div>
    );
}

function SimpleNotifications() {
    const notifications = useAppSelector(getNotifications);
    const dispatch = useAppDispatch();

    return (
        <div className="flex justify-center">
            <div className="flex max-w-sm flex-col gap-2.5">
                <AnimatePresence initial={false} mode="popLayout">
                    {notifications.map((notification) => (
                        <motion.div
                            key={notification.id}
                            layout
                            initial={{opacity: 0, y: 50, scale: 0.3}}
                            animate={{opacity: 1, y: 0, scale: 1}}
                            exit={{opacity: 0, scale: 0.5, transition: {duration: 0.2}}}
                            className="flex justify-center"
                        >
                            <SimpleNotification
                                notification={notification}
                                onDismiss={
                                    notification.permanent === true
                                        ? () =>
                                              dispatch(
                                                  dismissNotification(notification),
                                              )
                                        : undefined
                                }
                            />
                        </motion.div>
                    ))}
                </AnimatePresence>
            </div>
        </div>
    );
}

// Tab type for SimpleTabs
export type SimpleTab<T extends string = string> = {
    id: T;
    label: string;
    Icon: React.ComponentType<{className?: string}>;
    isHidden?: boolean;
    hasErrors?: boolean;
};

// Tab layout constants (in pixels)
// TAB_WIDTH: w-20 = 5rem = 80px
// TAB_GAP: gap-1 = 0.25rem = 4px
// TAB_PADDING: p-2 = 0.5rem = 8px
const TAB_WIDTH = 80;
const TAB_GAP = 4;
const TAB_PADDING = 8;
const TAB_TOTAL_WIDTH = TAB_WIDTH + TAB_GAP;

// Using const type parameter (TS 5.0+) to infer literal tab IDs from the array
type TabId<T extends readonly SimpleTab[]> = T[number]["id"];

interface SimpleTabsProps<T extends readonly SimpleTab[]> {
    tabs: T;
    selectedTab: TabId<T>;
    onSelectTab: (tab: TabId<T>) => void;
}

// Shared tab content renderer - receives pre-filtered visible tabs
function TabContent({
    visibleTabs,
    selectedTab,
    onSelectTab,
    selectedIndex,
}: {
    visibleTabs: SimpleTab[];
    selectedTab: string;
    onSelectTab: (tab: string) => void;
    selectedIndex: number;
}) {
    return (
        <div
            className="
                inline-flex items-center gap-1 rounded-3xl border border-gray-100/80
                bg-white/50 p-2 backdrop-blur-md
                dark:border-slate-700/80 dark:bg-slate-800/50
            "
        >
            <motion.div
                className="
                    absolute inset-y-2 w-20 rounded-2xl bg-gray-200
                    dark:bg-slate-600
                "
                initial={false}
                animate={{left: TAB_PADDING + selectedIndex * TAB_TOTAL_WIDTH}}
                transition={{type: "spring", stiffness: 500, damping: 35}}
            />
            {visibleTabs.map((tab) => {
                const {id, label, Icon} = tab;
                const isSelected = selectedTab === id;
                const colorClass = isSelected
                    ? "text-gray-900 dark:text-slate-100"
                    : tab.hasErrors === true
                      ? "text-red-500 dark:text-red-400"
                      : "text-gray-600 dark:text-slate-400";

                return (
                    <button
                        key={id}
                        type="button"
                        onClick={() => {
                            void hardware.haptic();
                            onSelectTab(id);
                        }}
                        className="
                            relative z-10 flex w-20 flex-col items-center gap-1 py-2
                        "
                    >
                        <Icon className={classNames("size-5", colorClass)} />
                        <span className={classNames("text-xs font-medium", colorClass)}>
                            {label}
                        </span>
                    </button>
                );
            })}
        </div>
    );
}

export function SimpleTabs<const T extends readonly SimpleTab[]>({
    tabs,
    selectedTab,
    onSelectTab,
}: SimpleTabsProps<T>) {
    const visibleTabs = tabs.filter((tab) => tab.isHidden !== true);
    const selectedIndex = visibleTabs.findIndex((tab) => tab.id === selectedTab);

    // Don't render if only 1 or no visible tabs (nothing to switch between)
    const showTabs = visibleTabs.length > 1;

    return (
        <AnimatePresence initial={false} mode="popLayout">
            {showTabs && (
                <motion.div
                    key="tabs"
                    layout
                    className="flex justify-center pt-2"
                    initial={{scale: 0.9, y: 10}}
                    animate={{
                        scale: 1,
                        y: 0,
                        transition: {type: "spring", stiffness: 400, damping: 30},
                    }}
                    exit={{scale: 0.9, y: 10, transition: {duration: 0.2}}}
                >
                    <TabContent
                        visibleTabs={visibleTabs}
                        selectedTab={selectedTab}
                        onSelectTab={onSelectTab}
                        selectedIndex={selectedIndex}
                    />
                </motion.div>
            )}
        </AnimatePresence>
    );
}

function useDarkMode(enabled: boolean) {
    const [isDarkMode, setIsDarkMode] = React.useState(
        () =>
            enabled &&
            typeof window !== "undefined" &&
            window.matchMedia("(prefers-color-scheme: dark)").matches,
    );
    const hasManualOverride = React.useRef(false);

    React.useEffect(() => {
        if (!enabled) return;
        const mediaQuery = window.matchMedia("(prefers-color-scheme: dark)");
        const handler = (e: MediaQueryListEvent) => {
            if (!hasManualOverride.current) {
                setIsDarkMode(e.matches);
            }
        };
        mediaQuery.addEventListener("change", handler);
        return () => mediaQuery.removeEventListener("change", handler);
    }, [enabled]);

    React.useEffect(() => {
        if (!enabled) return;
        document.documentElement.classList.toggle("dark", isDarkMode);
        return () => {
            document.documentElement.classList.remove("dark");
        };
    }, [enabled, isDarkMode]);

    const toggle = React.useCallback(() => {
        hasManualOverride.current = true;
        setIsDarkMode((prev) => !prev);
    }, []);

    return {isDarkMode, toggle};
}

function DarkModeToggle(props: {isDarkMode: boolean; toggle: () => void}) {
    return (
        <motion.div
            className="absolute right-0"
            style={{transformOrigin: "right center"}}
        >
            <button
                type="button"
                onClick={props.toggle}
                className="
                    p-1 text-gray-400 transition-colors
                    hover:text-gray-600
                    dark:text-slate-400
                    dark:hover:text-slate-200
                "
                aria-label={
                    props.isDarkMode ? "Switch to light mode" : "Switch to dark mode"
                }
            >
                {props.isDarkMode ? (
                    <SunIcon className="size-5" />
                ) : (
                    <MoonIcon className="size-5" />
                )}
            </button>
        </motion.div>
    );
}

// The header shrink curve: bounded rewinding scroll with a dead zone. The one
// source for everything that scales with the header — the plain title, the
// menu icon, and any titleSection that drives its own shrink (the walking
// club's wordmark) — so all of it moves on the same physics.
export const useTitleScale = (scrollYBoundedProgress: MotionValue<number>) => {
    const delayedProgress = useTransform(
        scrollYBoundedProgress,
        [0, 0.5, 1],
        [0, 0, 1],
    );
    return useTransform(delayedProgress, [0, 1], [1, 0.8]);
};

function SimpleSceneContent({
    scrollY,
    scrollYBoundedProgress,
    back,
    options,
    view,
    tabs,
    preparedActions,
    isModal,
    transition,
    darkModeToggle,
    isDarkMode,
}: {
    scrollY: MotionValue<number>;
    scrollYBoundedProgress: MotionValue<number>;
    back: {title: string; onClick: () => void} | null;
    options: RouterRenderPropsArgs["options"];
    view: React.ReactNode;
    tabs: RouterRenderPropsArgs["tabs"];
    preparedActions: ReturnType<typeof useActions>;
    isModal: boolean;
    transition: TransitionFn;
    darkModeToggle: React.ReactNode;
    isDarkMode: boolean;
}) {
    const isMenuOpen = useAppSelector((state) => state.scenes.isMenuOpen);
    const dispatch = useAppDispatch();
    const toggleMenu = () => dispatch({type: "TOGGLE_MENU"});

    const simpleTabs = tabs
        .filter(
            (tab): tab is typeof tab & {icon: Exclude<typeof tab.icon, false>} =>
                tab.icon !== false,
        )
        .map((tab) => ({
            id: tab.title,
            label: tab.title,
            Icon:
                typeof tab.icon === "string"
                    ? (props: {className?: string}) => (
                          <SimpleIcon
                              icon={tab.icon as IconType}
                              className={props.className}
                          />
                      )
                    : tab.icon,
            isHidden: "isHidden" in tab ? tab.isHidden : undefined,
            hasErrors: tab.hasErrors,
        }));
    const hasTabs = simpleTabs.length > 1;

    const hasBottomSection = options.bottomSection != null;
    const hasToolbar = options.toolbarYouAreInChargeOfTransparency != null;
    const hasActions = preparedActions.filter((a) => a != null).length > 0;

    // Tabs and actions can coexist (rendered side by side), but everything
    // else is mutually exclusive.
    const exclusiveCount = [hasBottomSection, hasToolbar, hasTabs || hasActions].filter(
        Boolean,
    ).length;
    if (exclusiveCount > 1) {
        throw new Error(
            "SimpleScene: bottomSection and toolbarYouAreInChargeOfTransparency cannot be combined with tabs/actions",
        );
    }

    // One column shared by the header content, the body and the bottom bar.
    // The bands behind them always run the full window width, so a scene
    // background is never cut off at the column's edge.
    const scene = options.scene?.kind === "SIMPLE" ? options.scene : undefined;
    const widthClass = scene?.width === "column" ? "max-w-md" : "max-w-3xl";
    const surface = scene?.surface ?? {kind: "fade"};
    const hasMenu = (scene?.nav ?? "menu") !== "none";

    // Background/blur: uses absolute scroll position (stays visible when scrolling up)
    const backgroundOpacity = useTransform(scrollY, [0, 50], [0, 0.9], {clamp: true});
    const blurAmount = useTransform(scrollY, [0, 50], [0, 1.5], {clamp: true});
    // Scale: uses bounded scroll (responsive to scroll direction, requires intentional scroll)
    const scale = useTitleScale(scrollYBoundedProgress);
    const backdropFilter = useTransform(blurAmount, (blur) => `blur(${blur}px)`);

    return (
        <>
            {/* Blur and background are separate siblings to work around a Safari
                compositing bug: Safari won't repaint a child's dark: class change
                if a parent has backdropFilter (compositor layer swallows the
                invalidation). Keeping them as siblings avoids this. */}
            <div className="sticky top-0 z-20">
                <motion.div className="absolute inset-0" style={{backdropFilter}} />
                {surface.kind === "custom" ? (
                    <motion.div
                        className="absolute inset-0"
                        style={{
                            background: isDarkMode
                                ? (surface.darkBackground ?? surface.background)
                                : surface.background,
                            opacity: backgroundOpacity,
                        }}
                    />
                ) : (
                    <motion.div
                        className="
                            absolute inset-0 bg-stone-50
                            dark:bg-slate-950
                        "
                        style={{opacity: backgroundOpacity}}
                    />
                )}
                <div
                    className={classNames(
                        "navigation-safe relative mx-auto w-full px-4 text-center",
                        widthClass,
                    )}
                >
                    <div className="relative flex items-center justify-center pb-2.5">
                        {hasMenu && isModal == false && back == null && (
                            <motion.div
                                className="absolute left-0"
                                style={{scale, transformOrigin: "left center"}}
                            >
                                <MenuIcon
                                    isMenuOpen={isMenuOpen}
                                    toggleMenu={toggleMenu}
                                />
                            </motion.div>
                        )}
                        {back != null && (
                            <motion.div
                                className="absolute left-0"
                                style={{scale, transformOrigin: "left center"}}
                            >
                                <TouchHandler
                                    ariaLabel={`Back to ${back.title}`}
                                    className="flex items-center gap-0.5"
                                    disableBackground
                                    haptic
                                    link={back.onClick}
                                >
                                    <SimpleIcon
                                        icon="custom-chevron-left"
                                        className="size-4"
                                    />
                                    <span className="ml-0.5 text-base font-normal">
                                        {back.title}
                                    </span>
                                </TouchHandler>
                            </motion.div>
                        )}
                        {/* Clearance for the side controls is SYMMETRIC and
                            lives here, in one place. It used to be split — a
                            pr-7 here plus a matching pl-7 that each titleSection
                            added for itself — which centres the box but not
                            what is drawn in it, and only as long as every
                            consumer remembers its half. Symmetric padding on
                            the centred element needs no counterpart: the middle
                            of the band stays the middle, and the title clears
                            the menu on the left and the toggle on the right. */}
                        <div
                            className={classNames(
                                "min-w-0 flex-1",
                                darkModeToggle != null && "px-7",
                            )}
                        >
                            {options.titleSection != null ? (
                                options.titleSection
                            ) : (
                                <motion.div
                                    className="text-xl font-normal"
                                    style={{scale}}
                                >
                                    {options.title}
                                </motion.div>
                            )}
                        </div>
                        {/* Out of flow, like the menu and back controls on the
                            left: a side control does not get a say in where the
                            middle is. */}
                        {darkModeToggle != null && (
                            <div className="absolute right-0">{darkModeToggle}</div>
                        )}
                    </div>
                    <div>{options.topSection}</div>
                </div>
            </div>
            <div
                className={classNames(
                    "relative mx-auto w-full flex-1 px-4",
                    widthClass,
                )}
            >
                {view}
            </div>
            {/* Notifications and bottom section - sticky container */}
            <div className="sticky bottom-0 z-20">
                <SimpleNotifications />
                {hasBottomSection && (
                    // No band paint (#1827) and no band spacing: the section
                    // owns its distance from the content above, so a scene's
                    // scroll-end gap is one token it controls (#1828).
                    <div className="pb-safe">
                        <div className={classNames("mx-auto w-full", widthClass)}>
                            {options.bottomSection}
                        </div>
                    </div>
                )}
                {hasToolbar && (
                    <div className="pb-safe transform-gpu">
                        {options.toolbarYouAreInChargeOfTransparency}
                    </div>
                )}
                {(hasTabs || hasActions) && (
                    <div className="pb-safe transform-gpu">
                        <div
                            className={classNames(
                                "mx-auto flex w-full items-center gap-2 px-8 pt-2",
                                widthClass,
                                hasTabs && hasActions
                                    ? "justify-between"
                                    : "justify-center",
                            )}
                        >
                            {hasTabs && (
                                <SimpleTabs
                                    tabs={simpleTabs}
                                    selectedTab={
                                        tabs.find((tab) => tab.isActive)?.title ??
                                        tabs[0].title
                                    }
                                    onSelectTab={(tabId) => {
                                        const tab = tabs.find((t) => t.title === tabId);
                                        if (tab == null) return;
                                        if (typeof tab.locator === "function") {
                                            tab.locator();
                                        } else {
                                            void transition(tab.locator);
                                        }
                                    }}
                                />
                            )}
                            {hasActions && (
                                <ActionBar
                                    actions={preparedActions}
                                    actionsStyle="contrast"
                                />
                            )}
                        </div>
                    </div>
                )}
            </div>
        </>
    );
}

export function SimpleScene({
    view,
    options,
    parent,
    actions,
    tabs,
    close,
    transition,
    ...rest
}: Pick<
    RouterRenderPropsArgs,
    "view" | "options" | "parent" | "locator" | "tabs" | "actions"
> & {
    close: ((result: null) => void) | null;
    isLoading: boolean;
    transition: TransitionFn;
}) {
    void rest;
    const backInfo =
        options.back != null && typeof options.back != "string"
            ? options.back
            : parent != null && options.back != null
              ? {locator: parent, title: options.back}
              : null;

    let back = null;

    if (backInfo != null) {
        if ("onClick" in backInfo) {
            back = {
                title: backInfo.title,
                onClick: backInfo.onClick,
            };
        } else {
            const backLocator = backInfo.locator;

            if ("linkFromAppToSite" in backLocator) {
                throw new Error("Unsupported locator for back");
            }
            back = {
                title: backInfo.title,
                onClick: () => {
                    void transition(backLocator);
                },
            };
        }
    } else if (typeof options.back == "string" && close != null) {
        back = {
            title: options.back,
            onClick: () => {
                close(null);
            },
        };
    }
    const isModal = close != null;

    const preparedActions = useActions(actions);
    const darkMode = useDarkMode(options.respectDarkMode === true);

    return (
        <>
            <title>{options.title}</title>
            <div
                className="
                    flex h-dvh w-full flex-col bg-stone-50 transition-colors
                    duration-200
                    dark:bg-slate-950
                "
            >
                <ScrollContainer>
                    {({scrollY, scrollYBoundedProgress}) => (
                        <SimpleSceneContent
                            scrollY={scrollY}
                            scrollYBoundedProgress={scrollYBoundedProgress}
                            back={back}
                            options={options}
                            isModal={isModal}
                            view={view}
                            tabs={tabs}
                            preparedActions={preparedActions}
                            transition={transition}
                            darkModeToggle={
                                options.respectDarkMode === true ? (
                                    <DarkModeToggle
                                        isDarkMode={darkMode.isDarkMode}
                                        toggle={darkMode.toggle}
                                    />
                                ) : null
                            }
                            isDarkMode={darkMode.isDarkMode}
                        />
                    )}
                </ScrollContainer>
            </div>
        </>
    );
}
